import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { superAdminService } from '../../Services/SuperAdminService';

const CYCLES = [
  { value: 'Monthly', months: 1 },
  { value: 'Quarterly', months: 3 },
  { value: 'Yearly', months: 12 }
];

// Local yyyy-mm-dd (what <input type="date"> uses); toISOString would shift
// the day for timezones ahead of UTC.
const toDateInput = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// Mirrors the API default: first payment due one billing cycle after start.
const defaultNextDue = (startDate, cycle) => {
  if (!startDate) return '';
  const [y, m, d] = startDate.split('-').map(Number);
  const months = CYCLES.find((c) => c.value === cycle)?.months || 1;
  return toDateInput(new Date(y, m - 1 + months, d));
};

const emptyForm = () => {
  const start = toDateInput(new Date());
  return {
    laboratoryName: '',
    laboratoryContactNumber: '',
    laboratoryEmail: '',
    laboratoryAddress: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    billingCycle: 'Monthly',
    amount: '3000',
    startDate: start,
    nextDueDate: defaultNextDue(start, 'Monthly')
  };
};

export default function AddLabDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(emptyForm);
  // Once the next due date is edited by hand, stop auto-recalculating it.
  const [nextDueTouched, setNextDueTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (!nextDueTouched && (name === 'billingCycle' || name === 'startDate')) {
        next.nextDueDate = defaultNextDue(next.startDate, next.billingCycle);
      }
      return next;
    });
    if (name === 'nextDueDate') setNextDueTouched(true);
  };

  const handleClose = () => {
    if (submitting) return;
    setForm(emptyForm());
    setNextDueTouched(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await superAdminService.createLab({
        lab: {
          laboratoryName: form.laboratoryName.trim(),
          laboratoryContactNumber: form.laboratoryContactNumber.trim(),
          laboratoryEmail: form.laboratoryEmail.trim(),
          laboratoryAddress: form.laboratoryAddress.trim()
        },
        admin: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phoneNumber: form.phoneNumber.trim(),
          password: form.password
        },
        subscription: {
          billingCycle: form.billingCycle,
          amount: Number(form.amount),
          startDate: form.startDate,
          nextDueDate: form.nextDueDate || null
        }
      });
      toast.success(`${form.laboratoryName} created`);
      setForm(emptyForm());
      setNextDueTouched(false);
      onCreated();
    } catch (error) {
      console.error('Failed to create laboratory:', error);
      toast.error(error?.response?.data?.message || 'Failed to create laboratory');
    } finally {
      setSubmitting(false);
    }
  };

  // Same label-above-input markup as the rest of the app's forms
  // (e.g. PatientRegistration).
  const field = (name, label, { hint, ...props } = {}) => (
    <div className="col-12 col-sm-6 col-lg-4">
      <label className="common-Labels mb-1 d-inline-block" htmlFor={`addlab-${name}`}>
        {label} <span className="common-Asterisk">*</span>
      </label>
      <input
        className="form-control form-control-sm common-Input"
        id={`addlab-${name}`}
        name={name}
        value={form[name]}
        onChange={handleChange}
        required
        {...props}
      />
      {hint && <div className="sa-form-hint mt-1 mb-0">{hint}</div>}
    </div>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <form onSubmit={handleSubmit} className="common-FontFamily">
        <DialogTitle className="sa-dialog-title">
          Add laboratory
          <IconButton onClick={handleClose} className="sa-dialog-close"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers className="sa-add-lab-content">
          <div className="sa-form-section">
            <h6 className="sa-form-heading">Laboratory</h6>
            <div className="row g-3">
              {field('laboratoryName', 'Lab Name', { autoFocus: true })}
              {field('laboratoryContactNumber', 'Contact Number')}
              {field('laboratoryEmail', 'Lab Email', { type: 'email' })}
              {field('laboratoryAddress', 'Address')}
            </div>
          </div>

          <div className="sa-form-section">
            <h6 className="sa-form-heading">Admin account</h6>
            <p className="sa-form-hint">The lab's first user, with the Admin role. They sign in with this email and password.</p>
            <div className="row g-3">
              {field('firstName', 'First Name')}
              {field('lastName', 'Last Name')}
              {field('email', 'Login Email', { type: 'email', autoComplete: 'off' })}
              {field('phoneNumber', 'Phone Number')}
              {field('password', 'Password', { type: 'password', autoComplete: 'new-password' })}
            </div>
          </div>

          <div className="sa-form-section">
            <h6 className="sa-form-heading">Subscription</h6>
            <div className="row g-3">
              <div className="col-12 col-sm-6 col-lg-4">
                <label className="common-Labels mb-1 d-inline-block" htmlFor="addlab-billingCycle">
                  Billing Cycle <span className="common-Asterisk">*</span>
                </label>
                <select
                  className="form-select form-select-sm common-Input"
                  id="addlab-billingCycle"
                  name="billingCycle"
                  value={form.billingCycle}
                  onChange={handleChange}
                  required
                >
                  {CYCLES.map((c) => <option key={c.value} value={c.value}>{c.value}</option>)}
                </select>
              </div>
              {field('amount', 'Amount per Cycle (PKR)', { type: 'number', min: 1, step: 'any' })}
              {field('startDate', 'Start Date', { type: 'date' })}
              {field('nextDueDate', 'First Payment Due', {
                type: 'date',
                min: form.startDate,
                hint: nextDueTouched ? 'Set manually' : 'One billing cycle after start'
              })}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? 'Creating…' : 'Create laboratory'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
